import React from 'react';
import IconClose from '@/assets/icons/icon-close.svg?react';
import { IconSimpleButton } from '@/components/buttons/IconSimpleButton/IconSimpleButton';
import { TextField } from '@/components/inspector-fields/TextField/TextField';
import { SelectField } from '@/components/dropdowns/SelectField/SelectField';
import { SelectItem } from '@/components/ui/select';
import { SizeField } from '@/components/inspector-fields/SizeField/SizeField';
import { Switch } from '@/components/inspector-fields/Switch/Switch';
import { useEditor } from '@craftjs/core';
import type { FormSubmitButtonProps } from '@builder4/shared/components/Form';
import type { ButtonVariant, ButtonSize } from '@builder4/shared/styles/constants';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import { FORM_SUBMIT_BUTTON_NODE_NAME } from '@/utils/constants';

interface ButtonFieldProps {
  onClose: () => void;
  styleDetached?: boolean; // true => detached (customizable)
  onToggleStyle?: () => void;
  submitButtonId?: string;
}

const ButtonField: React.FC<ButtonFieldProps> = ({ onClose, submitButtonId }) => {
  const { actions, buttonProps } = useEditor((_, query) => {
    if (!submitButtonId) {
      return { buttonProps: {} as FormSubmitButtonProps };
    }
    const node = query.node(submitButtonId).get();
    return {
      buttonProps: (node?.data.props || {}) as FormSubmitButtonProps,
    };
  });

  const { setBPProp, getBPProp } = useBreakpointAwareProp(FORM_SUBMIT_BUTTON_NODE_NAME);

  // Read props using breakpoint-aware getter
  const text = getBPProp(buttonProps, 'text') || 'Submit';
  const variant = getBPProp(buttonProps, 'variant') || 'primary';
  const size = getBPProp(buttonProps, 'size') || 'small';
  const fullWidth = getBPProp(buttonProps, 'fullWidth');

  // Update the specific button node's props
  const setProp = (callback: (props: FormSubmitButtonProps) => void) => {
    if (submitButtonId) {
      actions.setProp(submitButtonId, callback);
    }
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProp((props: FormSubmitButtonProps) => {
      setBPProp(props, 'text', e.target.value || 'Submit');
    });
  };

  const handleVariantChange = (value: string) => {
    setProp((props: FormSubmitButtonProps) => {
      setBPProp(props, 'variant', value as ButtonVariant);
    });
  };

  const handleSizeChange = (value: ButtonSize) => {
    setProp((props: FormSubmitButtonProps) => {
      setBPProp(props, 'size', value);
    });
  };

  const handleFullWidthChange = (checked: boolean) => {
    setProp((props: FormSubmitButtonProps) => {
      setBPProp(props, 'fullWidth', checked);
    });
  };

  return (
    <div className="bg-neutral-0 inline-flex h-[480px] max-h-[480px] w-[248px] flex-col overflow-hidden rounded-lg border border-neutral-100 shadow-[0px_12px_24px_0px_rgba(0,0,0,0.12)]">
      <div className="flex h-10 min-h-10 w-full items-center justify-between border-b border-neutral-100 px-3">
        <div className="text-xs leading-5 font-semibold text-neutral-900">Button</div>
        <IconSimpleButton aria-label="Close" icon={IconClose} size={24} onClick={onClose} />
      </div>
      <div className="flex min-h-0 w-full flex-1 flex-col border-t-0 border-neutral-100">
        <div className="no-scrollbar h-[440px] flex-1 overflow-y-auto">
          <div className="flex flex-col gap-3 px-3 pt-3 pb-3">
            <TextField
              className="flex w-full flex-row items-center gap-2"
              classNameInputParent=""
              inputValue={text}
              placeholderText=""
              state="default"
              text="Text"
              type="text"
              onChange={handleTextChange}
            />
            <SelectField
              text="Type"
              size="large"
              className="flex w-full flex-row items-center gap-2"
              classNameTrigger="w-full"
              defaultValue={variant}
              onValueChange={handleVariantChange}
            >
              <SelectItem value="primary">Primary</SelectItem>
              <SelectItem value="secondary">Secondary</SelectItem>
              <SelectItem value="accent">Accent</SelectItem>
            </SelectField>
            <SizeField className="w-full" size={size} text="Size" onClick={handleSizeChange} />
            <Switch
              checked={fullWidth}
              onCheckedChange={handleFullWidthChange}
              label="Full Width"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ButtonField;
